import { useEffect, useRef, useState } from 'react';
import ManualInvoiceStepIndicator from '../../admin/invoices/ManualInvoiceStepIndicator';
import useReducedMotion from '../../../hooks/ai/useReducedMotion';

// Builders-modern (docs/mockups/builders-modern-2026-09-27.html "stepper"):
// the active dot gets a brief scale "pop" on every step advance
// (`.sp-item.act .sp-dot.pop` there). ManualInvoiceStepIndicator already
// crossfades its connector color and the bar below already tweens its width
// at the exact timing/easing the mockup calls for (`width 240ms
// cubic-bezier(0.16, 1, 0.3, 1)`, unconditionally, for every consumer) — the
// dot pop is the one animated atom the mockup has that this component
// lacked. ManualInvoiceStepIndicator.js is not owned by this change, so the
// pop is applied via a scoped CSS selector reaching into its rendered DOM
// (`[aria-current="step"] > span:first-of-type` — the active step's dot is
// always that pill's first child span) from a class toggled on THIS
// component's own wrapper, instead of editing that file. Opt-in via
// `animated` (default false = today's exact output, asserted in
// WizardStepProgress.animated.test.js's "unchanged by default" case).
let popStyleInjected = false;
const POP_CLASS = 'cvx-wizard-step-pop-armed';
function ensurePopStyle() {
  if (popStyleInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
    @keyframes cvxWizardStepDotPop { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
    .${POP_CLASS} [aria-current="step"] > span:first-of-type {
      animation: cvxWizardStepDotPop 260ms cubic-bezier(0.34, 1.56, 0.64, 1);
    }
  `;
  document.head.appendChild(style);
  popStyleInjected = true;
}

/**
 * Numbered stepper — ONE progress indicator (founder, 2026-09-27: the thin
 * bar that used to sit under the pills repeated the same information and
 * read as two progress bars stacked; it is gone from every wizard). The
 * pills' connector already fills as you advance. `data-step` / `data-total`
 * stay on the wrapper for tests and E2E.
 *
 * `animated` (default false, opt-in): adds the active dot's "pop" bounce on
 * every step advance (see comment above). Every existing consumer (transfer
 * wizards, credits, bulk pickers) omits this prop and renders byte-identical
 * to before.
 *
 * `borderless` (default false, opt-in): drops the indicator's bottom hairline so a
 * caption row placed under it (full-screen flows, phone) owns the divider.
 *
 * `flush` (default false, opt-in, KAN-1182): no padding / background / hairline, for a
 * padded pop-up that already has its own gutter (the MFA + password pop-ups).
 *
 * `kbTuck` (default false, opt-in): marks the bar for the phone keyboard tuck
 * (KAN-552 1e, `modalStepTuck`) — only cards with a low text field pass it.
 */
export default function WizardStepProgress({ currentStep, steps, compact = false, animated = false, kbTuck = false, borderless = false, flush = false }) {
  const reduced = useReducedMotion();
  const total = Math.max(1, steps?.length || 1);

  const mountedRef = useRef(false);
  const [popArmed, setPopArmed] = useState(false);

  useEffect(() => {
    if (!animated || reduced) return undefined;
    if (!mountedRef.current) {
      mountedRef.current = true;
      return undefined;
    }
    ensurePopStyle();
    setPopArmed(true);
    const t = setTimeout(() => setPopArmed(false), 260);
    return () => clearTimeout(t);
  }, [currentStep, animated, reduced]);

  return (
    <div
      className={popArmed ? POP_CLASS : undefined}
      data-testid="wizard-progress"
      data-cvx-kb-tuck={kbTuck ? '' : undefined}
      data-step={currentStep}
      data-total={total}
    >
      <ManualInvoiceStepIndicator compact={compact} borderless={borderless} flush={flush} currentStep={currentStep} steps={steps} />
    </div>
  );
}
